import React from 'react'
import { useSnackbar } from 'notistack'
import ToastTemplate from './templates/toast-template'
import ToastBankingTemplate from './templates/toast-banking-template'
import { randomString } from '@/utils/crc-strings'
import { capturePostHogEvent } from '@/modules/posthog/posthog-tracking'
import { buildToastErrorProperties } from '@/modules/posthog/error-tracking'

let useSnackbarRef = null
let toastKeys = []
let exitingToastKeys = []

const toastListeners = new Set()

const notifyToastListeners = () => {
  toastListeners.forEach(listener => {
    listener({
      toastKeys: [...toastKeys],
      exitingToastKeys: [...exitingToastKeys],
    })
  })
}

const addToast = (key) => {
  toastKeys = toastKeys.filter(toastKey => toastKey !== key)
  exitingToastKeys = exitingToastKeys.filter(toastKey => toastKey !== key)
  toastKeys.push(key)
  notifyToastListeners()
}

const startToastExit = (key) => {
  toastKeys = toastKeys.filter(toastKey => toastKey !== key)

  if (!exitingToastKeys.includes(key)) {
    exitingToastKeys.push(key)
  }

  notifyToastListeners()
}

const removeToast = (key) => {
  const hasToast = toastKeys.includes(key) || exitingToastKeys.includes(key)

  if (!hasToast) return

  toastKeys = toastKeys.filter(toastKey => toastKey !== key)
  exitingToastKeys = exitingToastKeys.filter(toastKey => toastKey !== key)
  notifyToastListeners()
}

const subscribeToToastStack = (listener) => {
  toastListeners.add(listener)
  listener({
    toastKeys: [...toastKeys],
    exitingToastKeys: [...exitingToastKeys],
  })

  return () => {
    toastListeners.delete(listener)
  }
}

const DEFAULT_TOAST_DURATIONS = {
  success: 3000,
  info: 5000,
  warning: 5000,
  error: 8000,
}

const findErrorMessage = (obj, equalsExpression) => {
  if (equalsExpression(obj)) return obj
  for (const [, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        const res = findErrorMessage(v[i], equalsExpression)
        if (res !== null && res !== undefined) return res
      }
    } else {
      if (v !== null && v !== undefined) {
        if (typeof (v) === 'object') {
          if (equalsExpression(v)) return v
          const res = findErrorMessage(v, equalsExpression)
          if (res !== null && res !== undefined) return res
        } else {
          const res = equalsExpression(v)
          if (res) return v
        }
      } else continue
    }
  }
}

export const toast = {

  /**
 * @typedef {Object} BankAccountInfo
 * @property {string} bankLogoSmall - The small logo of the bank.
 * @property {string} bicCode - The BIC code of the bank.
 * @property {string} bankName - The name of the bank.
 * @property {string} accountIban - The IBAN of the bank account.
 * @property {string} accountName - The name of the bank account.
 * @property {boolean} [showOnlyBankName=false] - A flag indicating whether to show only the bank name.
 */

  /**
   * @typedef ToastOption
   * @property {string} [message]
   * @property {string} [title]
   * @property {'success'|'warning'|'error'|'info'|'closeIcon'} [type]
   * @property {'handshake'|'at'|'alert'|'banking'|'euro'} [icon] - only works if type is 'info'
   * @property {string} [key] - unique string
   * @property {any} [ref]
   * @property {any} [error]
   * @property {BankAccountInfo} [bankAccountInfo]
   * @property {boolean} [autoDismiss]
   * @property {number} [autoHideDuration]
   */

  /**
   * @param {ToastOption & {[Object]} option
   * @returns void
   */
  showToast({ message, title = undefined, type = 'success', icon = undefined, key = randomString(), ref = undefined, error = undefined, bankAccountInfo = undefined, autoDismiss = true, ...snackbarProps }) {
    let finalMessage = error?.message || message

    if (error?.body?.meta || error?.body || error) {
      const errorObject = findErrorMessage(error.body?.meta || error.meta || error, e => e.message) || {}
      finalMessage = errorObject.message || message
      type = 'error'
    }

    let toastDuration = snackbarProps.autoHideDuration

    if (!autoDismiss) toastDuration = null
    else if (toastDuration === undefined) {
      toastDuration =
        DEFAULT_TOAST_DURATIONS[type] ?? 6000
    }

    // no message/title: toast texts are free text and may contain customer names or e-mail addresses.
    // Error toasts are the errors the user actually sees, so they carry status code, error name/code
    // and the (id-less) endpoint to make them searchable in PostHog.
    const analyticsProperties = {
      type,
      icon,
      key,
      autoDismiss,
      autoHideDuration: toastDuration,
      isBankingToast: Boolean(bankAccountInfo),
      ...(error ? buildToastErrorProperties(error) : {}),
    }

    addToast(key)

    const originalOnExit = snackbarProps.onExit
    const originalOnExited = snackbarProps.onExited

    snackbarProps.onExit = (...args) => {
      startToastExit(key)
      originalOnExit?.(...args)
    }

    snackbarProps.onExited = (...args) => {
      removeToast(key)
      originalOnExited?.(...args)
    }

    snackbarProps.autoHideDuration = null

    if (bankAccountInfo) {
      const { bankLogoSmall, bicCode, bankName, accountIban, accountName, showOnlyBankName = false } = bankAccountInfo
      const content = (<ToastBankingTemplate
        title={title}
        type={type}
        snackBarKey={key}
        message={finalMessage}
        showOnlyBankName={showOnlyBankName}
        bankLogoUrl={bankLogoSmall}
        bicCode={bicCode}
        bankName={bankName}
        accountIban={accountIban}
        accountName={accountName}
        ref={ref}
        autoHideDuration={toastDuration}
        subscribeToToastStack={subscribeToToastStack}
        removeToast={removeToast} />)
      useSnackbarRef.enqueueSnackbar(key, { key, content, ...snackbarProps, variant: type })
      capturePostHogEvent('Toast shown', analyticsProperties)
      return
    } else {
      const content = <ToastTemplate
        snackBarKey={key}
        message={finalMessage}
        title={title}
        type={type}
        ref={ref}
        icon={icon}
        {...snackbarProps}
        autoHideDuration={toastDuration}
        subscribeToToastStack={subscribeToToastStack}
        removeToast={removeToast}
      />
      useSnackbarRef.enqueueSnackbar(finalMessage, { title, key, icon, content, ...snackbarProps, variant: type })
      capturePostHogEvent('Toast shown', analyticsProperties)
    }
  },
}

export const SnackbarUtilsConfigurator = () => {
  useSnackbarRef = useSnackbar()
  return null
}
